import { useRef, useState, type DragEvent } from 'react'
import type { MediaAssetPublic } from '@xingroute/schema'
import { useLocale } from '../../locale/LocaleContext.js'
import type { DraftMediaItem } from '../../lib/routeEditSession.js'
import { AuthorizedMediaImage } from '../AuthorizedMediaImage.js'

// EditableMediaSection — right-column media management for the route editor.
// Draft-based: uploads are staged locally (File + objectURL preview),
// deletes are marked — nothing hits the server until the user confirms save.

export interface EditableMediaSectionProps {
  media: MediaAssetPublic[]
  uploading: boolean
  mediaToken?: string | null
  disabled?: boolean
  // Draft props
  pendingUploads?: DraftMediaItem[]
  removedIds?: ReadonlySet<string>
  // Callbacks (draft-mode)
  onAddFile: (file: File) => void
  onMarkRemove?: (mediaId: string) => void
  onRemovePending?: (draftId: string) => void
  onReorder?: (next: MediaAssetPublic[]) => void
  onFocus: (mediaId: string) => void
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm'

export function EditableMediaSection({
  media,
  uploading,
  mediaToken,
  disabled = false,
  pendingUploads = [],
  removedIds,
  onAddFile,
  onMarkRemove,
  onRemovePending,
  onReorder,
  onFocus,
}: EditableMediaSectionProps) {
  const { t, locale } = useLocale()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [fileDropActive, setFileDropActive] = useState(false)

  // Filter out removed items
  const visibleMedia = removedIds ? media.filter((m) => !removedIds.has(m.id)) : media

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || disabled) return
    for (const file of Array.from(files)) {
      onAddFile(file)
    }
  }

  function handleDropFiles(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setFileDropActive(false)
    if (disabled) return
    handleFiles(event.dataTransfer.files)
  }

  function handleThumbDragStart(index: number) {
    if (disabled || !onReorder) return
    setDragIndex(index)
  }

  function handleThumbDragOver(event: DragEvent<HTMLLIElement>, index: number) {
    if (disabled || !onReorder || dragIndex === null) return
    event.preventDefault()
    setDragOverIndex(index)
  }

  function handleThumbDrop(event: DragEvent<HTMLLIElement>, index: number) {
    if (disabled || !onReorder || dragIndex === null) return
    event.preventDefault()
    if (dragIndex === index) {
      setDragIndex(null)
      setDragOverIndex(null)
      return
    }
    const next = media.slice()
    const moved = next[dragIndex]
    if (!moved) return
    next.splice(dragIndex, 1)
    next.splice(index, 0, moved)
    onReorder(next)
    setDragIndex(null)
    setDragOverIndex(null)
  }

  function handleThumbDragEnd() {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  return (
    <section className="space-y-3" data-testid="intro-media-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[11px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
          {t('editor.media.title')}
        </h2>
        {visibleMedia.length > 1 && !disabled && onReorder && (
          <span className="text-[10px] uppercase tracking-wide text-[var(--xr-text-muted)]">
            {t('editor.media.reorderHint')}
          </span>
        )}
      </div>

      {disabled ? (
        <div className="rounded-[var(--xr-radius-md)] border-2 border-dashed border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)]/30 p-6 text-center text-xs text-[var(--xr-text-muted)]">
          {t('editor.media.createGate')}
        </div>
      ) : (
        <>
          <input
            ref={fileRef}
            id="editor-media-upload-input"
            name="editor-media-upload"
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            data-testid="editor-media-upload-input"
            onChange={(event) => {
              handleFiles(event.target.files)
              event.target.value = ''
            }}
          />

          <ol className="grid grid-cols-5 gap-1.5" data-testid="editor-media-grid">
            {visibleMedia.map((item, index) => {
              const isDragOver = dragOverIndex === index && dragIndex !== index
              const isDragSource = dragIndex === index
              return (
                <li
                  key={item.id}
                  draggable={Boolean(onReorder)}
                  onDragStart={() => handleThumbDragStart(index)}
                  onDragOver={(event) => handleThumbDragOver(event, index)}
                  onDrop={(event) => handleThumbDrop(event, index)}
                  onDragEnd={handleThumbDragEnd}
                  className={
                    'group relative aspect-square overflow-hidden rounded-[var(--xr-radius-md)] border bg-[var(--xr-bg-surface-elev)] transition ' +
                    (isDragOver
                      ? 'border-[var(--xr-brand-primary)] ring-2 ring-[var(--xr-brand-primary)]'
                      : 'border-[var(--xr-border-default)]') +
                    (isDragSource ? ' opacity-50' : '')
                  }
                  data-testid="editor-media-thumb"
                >
                  <button
                    type="button"
                    onClick={() => onFocus(item.id)}
                    aria-label={t('editor.media.focusAria')}
                    className="block h-full w-full"
                  >
                    {item.kind === 'image' ? (
                      <AuthorizedMediaImage
                        source={item.thumb_url ?? item.blob_url}
                        token={mediaToken}
                        alt={item.alt_text ?? ''}
                        className="h-full w-full object-cover"
                        draggable={false}
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[var(--xr-media-bg)] text-xs text-[var(--xr-text-muted)]">
                        ▶ {locale === 'zh' ? '视频' : 'Video'}
                      </div>
                    )}
                  </button>
                  {onMarkRemove && (
                    <button
                      type="button"
                      aria-label={t('editor.media.deleteAria')}
                      onClick={(event) => {
                        event.stopPropagation()
                        onMarkRemove(item.id)
                      }}
                      className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--xr-danger)] bg-[var(--xr-bg-surface)]/90 text-xs font-bold leading-none text-[var(--xr-danger)] opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                      data-testid="editor-media-delete"
                    >
                      ×
                    </button>
                  )}
                  {index === 0 && (
                    <span
                      className="pointer-events-none absolute bottom-0.5 left-0.5 rounded-[var(--xr-radius-sm)] bg-[var(--xr-brand-primary)]/90 px-1 py-0 text-[8px] font-medium leading-tight text-white"
                      aria-label={locale === 'zh' ? '默认大图' : 'Hero'}
                    >
                      {locale === 'zh' ? '默' : '★'}
                    </span>
                  )}
                </li>
              )
            })}
            {/* Pending uploads (draft, not yet on server) */}
            {pendingUploads.map((item) => (
              <li
                key={item.id}
                className="group relative aspect-square overflow-hidden rounded-[var(--xr-radius-md)] border border-dashed border-[var(--xr-brand-primary)]/40 bg-[var(--xr-brand-primary-soft)]"
                data-testid="editor-media-pending"
              >
                {item.file.type.startsWith('video/') ? (
                  <div className="flex h-full w-full items-center justify-center bg-[var(--xr-media-bg)] text-xs text-[var(--xr-text-muted)]">
                    {locale === 'zh' ? '视频' : 'Video'}
                  </div>
                ) : (
                  <img
                    src={item.previewUrl}
                    alt=""
                    className="h-full w-full object-cover opacity-80"
                  />
                )}
                {onRemovePending && (
                  <button
                    type="button"
                    onClick={() => onRemovePending(item.id)}
                    aria-label={t('editor.media.removePendingAria')}
                    className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--xr-danger)] bg-[var(--xr-bg-surface)]/90 text-xs font-bold leading-none text-[var(--xr-danger)] opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                )}
                <span className="pointer-events-none absolute bottom-0.5 left-0.5 rounded-[var(--xr-radius-sm)] bg-[var(--xr-brand-primary)]/90 px-1 py-0 text-[8px] font-medium leading-tight text-white">
                  {locale === 'zh' ? '待传' : 'Pending'}
                </span>
              </li>
            ))}
            {/* Upload control — a thumbnail-sized square that always sits as
                the last cell of the grid (5 per row, wrapping). */}
            <li className="aspect-square" data-testid="editor-media-upload-tile">
              <label
                htmlFor="editor-media-upload-input"
                role="button"
                tabIndex={0}
                aria-label={t('editor.media.uploadAria')}
                onDragOver={(event) => {
                  event.preventDefault()
                  setFileDropActive(true)
                }}
                onDragLeave={() => setFileDropActive(false)}
                onDrop={handleDropFiles}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    fileRef.current?.click()
                  }
                }}
                title={t('editor.media.dropHint')}
                className={
                  'flex h-full w-full cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[var(--xr-radius-md)] border-2 border-dashed text-center transition ' +
                  (fileDropActive
                    ? 'border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)]/40 text-[var(--xr-brand-primary)]'
                    : 'border-[var(--xr-border-strong)] text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface-elev)]')
                }
                data-testid="editor-media-dropzone"
              >
                <span className="text-lg leading-none" aria-hidden="true">
                  +
                </span>
                {uploading && (
                  <span className="px-1 text-[9px] leading-tight">
                    {t('editor.media.uploading')}
                  </span>
                )}
              </label>
            </li>
          </ol>
        </>
      )}
    </section>
  )
}
